/**
 * Purpose: React entry point for the ada portal SPA. Sets up the auth
 *          provider, gates the app behind <RequireAuth> + <LoginScreen>,
 *          and renders <App/>.
 *
 * Implementation notes:
 *   - Mirror of the trace-admin entry. Uses the same shared
 *     `@ada/auth-react` config and `@ada/auth-shell` callback.
 *   - No TanStack Query: the portal has no /api calls — it only renders
 *     a static link list whose visibility is derived from the OIDC ID
 *     token's project-role claim via `useHasRole`.
 *
 * Created: 2026-05-12 10:00
 *
 * Changelog:
 *   - 2026-05-12 10:00 — Initial version.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  AuthProvider,
  LoginScreen,
  RequireAuth,
  type AuthConfig,
} from '@ada/auth-react';

import { App } from './App.js';
import './index.css';

const authConfig: AuthConfig = {
  issuer: requireEnv('VITE_ZITADEL_ISSUER'),
  clientId: requireEnv('VITE_ZITADEL_CLIENT_ID'),
  orgId: requireEnv('VITE_ZITADEL_ORG_ID'),
  redirectUri: requireEnv('VITE_ZITADEL_REDIRECT_URI'),
  postLogoutRedirectUri: requireEnv('VITE_ZITADEL_POST_LOGOUT_REDIRECT_URI'),
};

function requireEnv(key: string): string {
  const v = import.meta.env[key];
  if (typeof v !== 'string' || v.length === 0) {
    throw new Error(
      `portal: missing build-time env var ${key}. Set it in the monorepo .env or your deploy environment.`,
    );
  }
  return v;
}

const root = document.getElementById('root');
if (!root) throw new Error('#root element missing from index.html');

createRoot(root).render(
  <StrictMode>
    <AuthProvider config={authConfig}>
      <RequireAuth fallback={<LoginScreen appName="ada · portal" />}>
        <App />
      </RequireAuth>
    </AuthProvider>
  </StrictMode>,
);
